import EventBus, { showToast, EVENTS, TOAST } from '@mimir/web/eventBus';
import type { PersonResource } from '@mimir/web/pages/person/person-interfaces';
import { ViewType } from '@mimir/web/pages/search/constants';
import { addRouterLink as addRouterLink_, getDefaultDateRangeFieldChoices } from '@mimir/web/pages/search/util';
import { doesItemHavePlaceholderThumbnail, getThumbUrl } from '@mimir/web/util/thumbnail';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { createPlaceholder, getItem, getItemFields, getLatestItemVersion } from '@mimir/web/util/api';
import type { UploadItem } from '@mimir/client-common/upload/UploadItem';
import type { SearchResultHit, Item, LoadedItem } from '@mimir/common/dto/searchResultHit';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import { ArchiveRestoreStatus } from '@mimir/common/dto/itemPropTypes';
import { ItemState } from '@mimir/common/dto/itemState';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { SearchCategoryCount, SortParameters} from '@mimir/common/dto/searchModels';
import { SearchDateOption, SortOptions, SortOrder, StaticFilterKeywords } from '@mimir/common/dto/searchModels';
import { clearFilters, extraInfoSticky, filterDelimiter, separateFiltersFromSearchString } from '@mimir/common/filters/Filters';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import sortBy from 'lodash/sortBy';
import type { AxiosError } from 'axios';
import Vue from 'vue';
import type { ActionContext, Commit, Dispatch, Module } from 'vuex';
import type { State } from './state';
import type { SearchState, ViewSpecificDetails } from './interfaces';
import type { FolderViewModel } from '@mimir/common/dto/folderModels';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { SearchModuleState } from './search-module-state';
import type { VueDeleteValue, VueSetValue } from '@mimir/common/util/general';
import type { MdfUpdateItem} from '@mimir/common/mdf/update';
import { handleMdfUpdatesToItem } from '@mimir/common/mdf/update';
import { SYSTEM_FIELD_ID_QUERYPARAM, TECHNICAL_FIELD_ID_QUERYPARAM } from '@mimir/common/search/interfaces';
import type { SearchResultViewModel } from '@mimir/common/search/interfaces';
import { generateRouterLink } from '../util/route';
import { folders } from '@mimir/web/globals/folders';
import { loadFolderPathForItem, loadFolderPaths } from '@mimir/web/globals/foldersUtils';
import { isInElectron, type ApplicationContext, isInAdobeApp } from '@mimir/common/context/applicationContext';
import { setDateFilterField } from '@mimir/web/util/filterUtils';
import { loadItemInQuickPreview } from './search-player-module';
import type { ItemMediaPlayerOps } from '@mimir/web/model/itemMediaPlayerOps';
import { getInjectionIndex } from '@mimir/common/websockets/wsItemInjection';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { updateWsItemInjectionCount } from '../globals/wsItemInjection';

const viewSpecificSettings: Record<ViewType, ViewSpecificDetails> = {
  [ViewType.LIST]: {
    itemsPerPage: 15
  },
  [ViewType.GRID]: {
    itemsPerPage: 30,
  },
  [ViewType.MAP]: {
    itemsPerPage: 100,
  },
  [ViewType.COMPACT]: {
    itemsPerPage: 100
  }
};

const searchModuleState: SearchModuleState = {
  itemsPerPage: 15,
  isLoadingSearch: false,
  typeCount: [],
  isRecycleBinSearch: false,
  searchForm: {
    searchString: '',
    rawSearchString: '',
    isFuzzy: false,
    atSameTime: false,
    showOnlyDeletedItems: false,
    sortBy: {
      property: SortOptions.ITEM_CREATED_DATE,
      order: SortOrder.DESCENDING
    },
    sortParamsList: [],
    dateTimeSearch: SearchDateOption.ANYTIME,
    defaultDateRangeField: StaticFilterKeywords.DateRange,
    useCustomSearchHook: false,
  },
  lastUsedSortBy: null,
  itemSearch: {
    searchString: '',
    startSearchFrom: 0,
    searchHasMoreHits: false,
    viewType: ViewType.LIST,
    total: 0,
    results: {
      count: 0,
      total: 0,
      from: 0,
      indexOfCurrentLoadedItemInHits: -1,
      typeCount: [],
      hits: [],
      dateHits: {},
      incompleteFolderCount: null,
    },
  },
  selectedSearchItems: [],
  lastSearchParams: {},
  customNoResultMessage: null,
  rightPaneWidthInPct: 25,
  listOfFolderIdsInLastSearchResult: [],
  pathsForNewFolderHitsHasBeenLoaded: false,
  allTypesFilter: false,
};

export const searchModule: Module<SearchModuleState, State> = {
  state: searchModuleState,
  mutations: {
    setCustomNoResultMessage: (state, payload: string | null) => state.customNoResultMessage = payload,
    setIndexOfCurrentLoadedItemInHits: (state, payload: number) => state.itemSearch.results.indexOfCurrentLoadedItemInHits = payload,
    isRecycleBinSearch: (state, payload: boolean) => state.isRecycleBinSearch = payload,
    resetSearch: (state) => {
      state.itemSearch.startSearchFrom = 0;
      state.itemSearch.total = 0;
      state.itemSearch.results = {
        count: 0,
        total: 0,
        from: 0,
        indexOfCurrentLoadedItemInHits: -1,
        typeCount: [],
        hits: [],
        dateHits: {},
        incompleteFolderCount: null,
      };
      state.typeCount = [];
    },
    clearSearchString: state => {
      state.searchForm.rawSearchString = '';
      state.searchForm.searchString = '';
    },
    setRightPaneWidthInPct: (state, payload: number) => state.rightPaneWidthInPct = payload,
    setTypeCount: (state, payload: SearchCategoryCount[]) => state.typeCount = payload,
    setRawSearchString: (state, value: string) => state.searchForm.rawSearchString = value,
    setSearchString: (state, value: string) => (state.searchForm.searchString = value),
    itemSearch: (state, value: SearchState) => (state.itemSearch = value),
    setItemsPerPage: (state, payload: number) => (state.itemsPerPage = payload),
    setSameTimeFilter: (state, payload: boolean) => state.searchForm.atSameTime = payload,
    setViewSpecificDetails: (state) => state.itemsPerPage = viewSpecificSettings[state.itemSearch.viewType ?? ViewType.LIST].itemsPerPage,
    setIsLoadingSearch: (state, payload: boolean) => state.isLoadingSearch = payload,
    setSortParamsList: (state, payload: SortParameters[]) => state.searchForm.sortParamsList = payload,
    setSearchFormSortParams: (state, payload: { params: SortParameters, doNotChangeLevel?: boolean }) => {
      const { params, doNotChangeLevel } = payload;
      if (state.itemSearch.viewType === ViewType.COMPACT) {
        const sortParamsList = state.searchForm.sortParamsList;
        if (!sortParamsList) {
          // no previous list. make a new one.
          state.searchForm.sortParamsList = [params];
        } else {
          // find if the old list already has this sort prop
          const previousIndex = sortParamsList.findIndex(sortParams => sortParams.property === params.property);
          if (previousIndex >= 0) {
            // if already there, remove it
            sortParamsList.splice(previousIndex, 1);
          }
          // At this point the param is removed from sortParamsList, if present in old list.
          // Now if order property is missing we should return. From header-context-menu user can clear column order.
          if (params.order === undefined || params.order === '') {
            return;
          }
          // should not change sort level when user only change sort order and the param has previousIndex > 0.
          if (doNotChangeLevel && previousIndex > 0) {
            sortParamsList.push(params);
          } else {
            // add it to the top as the most significant sort prop
            sortParamsList.unshift(params);
          }

          // if list grows more than max, truncate it to keep most recent sort props
          const maxSortCols = 2;
          if (sortParamsList.length > maxSortCols) {
            sortParamsList.length = maxSortCols;
          }
        }

        // do not modify the old sortBy prop
        return;
      }

      state.searchForm.sortBy = params;
    },
    setSearchDate:(state, payload: SearchDateOption) => {
      state.searchForm.dateTimeSearch = payload;
    },
    setDefaultDateRangeField:(state, payload: string | undefined) => {
      if (!payload) {
        return;
      }
      state.searchForm.defaultDateRangeField = payload;
    },
    setLastUsedSortBy: (state, payload: SortParameters | null) => state.lastUsedSortBy = payload,
    lastSearchParams: (state, payload: Record<string, string>) => state.lastSearchParams = payload,
    setRestoreMediaCompletedInSearchResult: (state, payload: { itemId: string, highRes: string, mediaOnlineUntil: string }) => {
      for (const item of state.itemSearch.results.hits) {
        if (item && item.archiveInfo && ((item.id === payload.itemId)
        || (item.virtualClipInfo && item.virtualClipInfo.masterClipId === payload.itemId))) {
          // Not setting highRes as SearchResultHit does not need the highRes link
          Vue.set(item.archiveInfo, 'restoreStatus', ArchiveRestoreStatus.Completed);
          Vue.set(item.archiveInfo, 'mediaOnlineUntil', payload.mediaOnlineUntil);
        }
      }
    },
    setMediaRestoreStatusInSearchResult: (state, payload: { itemId: string, restoreStatus: ArchiveRestoreStatus }) => {
      for (const item of state.itemSearch.results.hits) {
        if (item && item.archiveInfo && ((item.id === payload.itemId)
          || (item.virtualClipInfo && item.virtualClipInfo.masterClipId === payload.itemId))) {
          Vue.set(item.archiveInfo, 'restoreStatus', payload.restoreStatus);
        }
      }
    },
    renameFolderInSearch: (state, payload: { id: string, newName: string }) => {
      const { id, newName } = payload;
      if (state.itemSearch.results) {
        const hit = state.itemSearch.results.hits.find(item => item.id === id);
        if (hit) {
          hit.name = newName;
        }
      }
    },
    injectFolderIntoSearchResult: (state, payload: SearchResultHit) => {
      if (state.itemSearch.results) {
        if (state.itemSearch.results.hits.some(hit => hit.id === payload.id)) {
          showToast('Folder already present in the current folder', TOAST.INFO);
          return;
        }

        payload.itemType = ItemType.FOLDER;

        const hasFolders = state.itemSearch.results.hits.some(hit => hit.itemType === ItemType.FOLDER);
        const hasNonFolders = state.itemSearch.results.hits.some(hit => hit.itemType !== ItemType.FOLDER);

        // Can safely insert at the start.
        if (!hasFolders) {
          state.itemSearch.results.hits.unshift(payload);
          return;
        }

        if (!hasNonFolders) {
          const unsorted = [...state.itemSearch.results.hits];
          unsorted.push(payload);
          state.itemSearch.results.hits = sortBy(unsorted, hit => hit.name);
        } else {
          const index = state.itemSearch.results.hits.findIndex(hit => hit.itemType !== ItemType.FOLDER);
          let unsorted = state.itemSearch.results.hits.splice(0, index);
          unsorted.push(payload);
          unsorted = sortBy(unsorted, hit => hit.name);
          state.itemSearch.results.hits = [...unsorted, ...state.itemSearch.results.hits];
        }
      }
    },
    injectItemIntoSearchResult: (state, { item, shouldUpdateExisting, currentFolder }: InjectItemIntoSearchPayload) => {
      if (state.itemSearch.results) {

        if (!doesItemBelongInCurrentFolder(item, currentFolder)) return;

        let firstNonFolderIndex = -1;
        for (let i = 0; i < state.itemSearch.results.hits.length; i++) {
          const hit = state.itemSearch.results.hits[i];
          if (hit.itemType !== ItemType.FOLDER && firstNonFolderIndex === -1) {
            firstNonFolderIndex = i;
          }
          // If the item already exists - don't inject.
          if (hit.id === item.id) {
            if (shouldUpdateExisting) Vue.set(state.itemSearch.results.hits, i, { ...hit, ...item });
            return;
          }
        }

        if (firstNonFolderIndex < 0) {
          state.itemSearch.results.hits.push(item);
        } else {
          state.itemSearch.results.hits.splice(firstNonFolderIndex, 0, item);
        }
        state.itemSearch.results.total++;
        state.itemSearch.total++;
      }
    },
    injectItemsIntoSearchResult: (state, payload: {items: SearchResultHit[], mdfs?: MetaDataSchema[], ignoreSorting?: boolean}) => {
      if (!state.itemSearch.results) return;
      const { items, ignoreSorting, mdfs = [] } = payload;

      let firstNonFolderIndex = 0;
      for (let i = 0; i < state.itemSearch.results.hits.length; i++) {
        if (state.itemSearch.results.hits[i].itemType !== ItemType.FOLDER) {
          firstNonFolderIndex = i;
          break;
        }
      }

      // For version search, we need to inject versions in the order they are received. Versions are not fetched through search api.
      // We always ignore websocket item creation message for version view. So we do not have to worry about memory bloating.
      if (ignoreSorting) {
        state.itemSearch.results.hits.splice(firstNonFolderIndex, 0, ...items);
        state.itemSearch.results.total += items.length;
        state.itemSearch.total += items.length;
        return;
      }

      const sortParamList = state.searchForm.sortParamsList as SortParameters[];
      const sortFieldsInfo = extractSortFieldsInfo(sortParamList, mdfs);
      const isCompactView = state.itemSearch.viewType === ViewType.COMPACT;
      const sorParams: SortParameters[] = isCompactView ? sortParamList : [state.searchForm.sortBy as SortParameters];
      const isAllItemsLoaded = state.itemSearch.results.hits.length === state.itemSearch.total;

      const itemsToBeInjected: {item: SearchResultHit, index: number}[] = [];
      for (const item of items) {
        const index = getInjectionIndex(item, sorParams, state.itemSearch.results.hits, isAllItemsLoaded, isCompactView, sortFieldsInfo);
        if (index !== null) {
          itemsToBeInjected.push({ item, index });
        }
      }
      if (itemsToBeInjected.length === 0) return;
      itemsToBeInjected.sort((a, b) => b.index - a.index);

      // Unnecessary memory bloating can happen when search view is left alone for a long time
      // while new items keep getting injected through WebSocket events. To prevent this, we
      // are evicting the bottom results from the currently loaded array before adding the new ones.
      const nonFolderItemsLength = state.itemSearch.results.hits.length - firstNonFolderIndex;
      if (state.itemSearch.results.hits.length > 100 && nonFolderItemsLength >= itemsToBeInjected.length) {
        state.itemSearch.results.hits.length -= itemsToBeInjected.length;
        state.itemSearch.results.total -= itemsToBeInjected.length;
      } else if (!isAllItemsLoaded) {
        // When we are injecting items into a search result and it is not fully loaded, we need to
        // adjust search parameters to consider the new items and fetching the next batch from appropriate index.
        // This is only relevant for continuation search.
        updateWsItemInjectionCount(itemsToBeInjected.length);
        state.itemSearch.startSearchFrom += itemsToBeInjected.length;
      }

      for (const { item, index } of itemsToBeInjected) {
        state.itemSearch.results.hits.splice(index, 0, item);
      }

      state.itemSearch.results.total += itemsToBeInjected.length;
      state.itemSearch.total += itemsToBeInjected.length;
    },
    removeFromSearchById: (state, id: string) => {
      if (state.itemSearch.results) {
        const index = state.itemSearch.results.hits.findIndex(item => item.id === id);
        if (index >= 0) {
          state.itemSearch.results.hits.splice(index, 1);
          state.itemSearch.results.total--;
          state.itemSearch.total--;
        }
      }
    },
    updateSearchHitById: (state, payload: UpdateSearchHitById) => {
      const { id, update, retrievedFields } = payload;
      /*
        `payload.retrievedFields` contains a list of keys fetched from the server.
        Since `undefined` values are not transmitted over the API, we combine the
        fetched keys with those available in the `payload.update` object.

        This ensures that the client state is updated with the most recent data
        while correctly handling stale state updates.
      */
      const uniqueKeys = new Set([...Object.keys(update), ...(retrievedFields ?? [])]);
      const keysToIterateOver = Array.from(uniqueKeys) as (keyof SearchResultHit)[];

      const searchHit = state.itemSearch.results.hits.find(hit => hit.id === id);
      if (!searchHit) {
        return;
      }
      const doesUpdateHaveMedia = keysToIterateOver.find(el => el === 'media');
      const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
      const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
      for (const prop of keysToIterateOver) {
        if (prop === 'metadata' && update.metadata !== undefined) {
          let metadataVersion = update.metadataVersion;
          if (metadataVersion == undefined) {
            console.warn('updateSearchHitById: metadata update without metadataVersion');
            metadataVersion = 0;
          }
          handleMdfUpdatesToItem(searchHit, update.metadata, metadataVersion, vueSet, vueDeleter);
          continue;
        }
        // in shouldPollForUpdates we use 'media' property as the source of truth if kelda live item's
        // thumbnail is available. but there is a slight window where thumbnail is available but item
        // doesn't yet have the 'media' property. if a user reloads the folder during this time period
        // the thumbnail flashes black. this check addresses this problem by checking if the item already
        // has a valid thumbnail url
        if (
          prop === 'thumbnail' &&
          doesUpdateHaveMedia &&
          !doesItemHavePlaceholderThumbnail(searchHit as Item)
        ) {
          continue;
        }

        Vue.set(searchHit, prop, update[prop]);
      }
    },
    replaceSearchHitById: (state, payload: ReplaceSearchHitById) => {
      const { newItem, itemIdToReplace } = payload;
      const hits = state.itemSearch.results.hits;
      const index = hits.findIndex(hit => hit.id === itemIdToReplace);
      if (index < 0) {
        return;
      }
      const wasSelected = hits[index].selected;
      Vue.set(hits, index, newItem);
      if (wasSelected)  {
        state.selectedSearchItems.push(newItem);
        Vue.set(newItem, 'selected', true);
      }
    },
    removeSelectedItemsFromSearchResult: (state, payload: { onlyFolders: boolean, excludeId: string }) => {
      const { onlyFolders, excludeId } = payload;
      const hits = state.itemSearch.results.hits;
      for (const selectedItem of state.selectedSearchItems.filter(hit => hit.id !== excludeId)) {
        const index = hits.findIndex(hit => hit.id === selectedItem.id);
        if (index >= 0) {
          const hit = hits[index];
          if (onlyFolders && hit.itemType !== ItemType.FOLDER) {
            continue;
          }
          hits.splice(index, 1);
        }
      }
    },
    addToSelectedSearchItems: (state, item: SearchResultHit | null | undefined) => {
      if (!item) return;

      const idx = state.selectedSearchItems.findIndex(selectedItem => selectedItem.id === item.id);
      if (idx === -1) {
        state.selectedSearchItems.push(item);
        Vue.set(item, 'selected', true);
      }
    },
    removeIdsFromSearchResult: (state, payload: { onlyFolders?: boolean, idsToRemove: string[] }) => {
      const { onlyFolders, idsToRemove } = payload;
      const hits = state.itemSearch.results.hits;
      for (const id of idsToRemove) {
        const index = hits.findIndex(hit => hit.id === id);
        if (index >= 0) {
          const hit = hits[index];
          if (onlyFolders && hit.itemType !== ItemType.FOLDER) {
            continue;
          }
          hits.splice(index, 1);
        }
      }
    },
    removeFromSelectedSearchItems: (state, item: SearchResultHit | null | undefined) => {
      if (!item) return;

      const idx = state.selectedSearchItems.findIndex(selectedItem => selectedItem.id === item.id);
      if (idx === -1) return;

      state.selectedSearchItems.splice(idx, 1);
      Vue.set(item, 'selected', false);
    },
    clearSelectedSearchItems: (state) => {
      if (!state.selectedSearchItems.length) return;
      state.selectedSearchItems.map((item: SearchResultHit) => {
        Vue.set(item, 'selected', false);
      });
      state.selectedSearchItems = [];
    },
    resetSearchResultArray: state => {
      state.itemSearch.results.count = 0;
      state.itemSearch.results.hits = [];
    },
    updateMetadataOfSearchHitItem: (state, payload: {id: string, metadata: ItemMetaData, metadataVersion: number }) => {
      const { id, metadata, metadataVersion } = payload;
      const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
      const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
      const itemFromSearch: MdfUpdateItem | undefined = state.itemSearch.results.hits.find(hit => hit.id === id);
      if (itemFromSearch) {
        handleMdfUpdatesToItem(itemFromSearch, metadata, metadataVersion, vueSet, vueDeleter);
      }
    },
    /**
     * Append search hits to itemSearch, and update isRecycleBinSearch and
     * typeCount.
     */
    appendSearchHits: async (state, payload: SearchResultViewModel) => {
      const searchState = state.itemSearch;
      searchState.total = payload.total;
      if (payload.from + payload.hits.length < payload.total) {
        searchState.searchHasMoreHits = true;
        searchState.startSearchFrom = payload.from + payload.hits.length;
      } else {
        searchState.searchHasMoreHits = false;
      }

      if (searchState.results && searchState.results.hits) {
        searchState.results.hits = searchState.results.hits.concat(payload.hits);
      } else {
        searchState.results = payload;
      }

      payload.hits.forEach(hit => {
        if (hit.itemType === ItemType.FOLDER) state.listOfFolderIdsInLastSearchResult.push(hit.id);
      });

      if (searchState.results.dateHits && !isEqual(searchState.results.dateHits, payload.dateHits)) {
        searchState.results.dateHits = payload.dateHits;
      }

      state.isRecycleBinSearch = payload.isRecycleBin;
      if (!payload.isRecycleBin) {
        state.typeCount = payload.typeCount;
      }
    },
    setPathsForNewFolderHitsHasBeenLoaded: (state, val: boolean) => {
      state.pathsForNewFolderHitsHasBeenLoaded = val;
    },
    setListOfFolderIdsInLastSearchResult: (state, payload: string[]) => {
      state.listOfFolderIdsInLastSearchResult = payload;
    },
    setAllTypesFilter: (state, payload: boolean) => state.allTypesFilter = payload,
  },
  getters: {
    rightPaneWidthInPct: state => state.rightPaneWidthInPct,
    customNoResultMessage: state => state.customNoResultMessage,
    indexOfCurrentLoadedItemInHits: state => state.itemSearch.results.indexOfCurrentLoadedItemInHits,
    isRecycleBinSearch: state => state.isRecycleBinSearch,
    searchString: state => state.searchForm.searchString,
    rawSearchString: state => state.searchForm.rawSearchString,
    itemsPerPage: state => state.itemsPerPage,
    searchForm: state => state.searchForm,
    itemSearch: state => state.itemSearch,
    isEmptySearch: state => isEmpty(state.itemSearch.results.hits),
    typeCount: state => state.typeCount,
    isLoadingSearch: state => state.isLoadingSearch,
    currentViewType: state => state.itemSearch.viewType,
    isSelectedSearchItem: state => (item: SearchResultHit) =>
      state.selectedSearchItems.some(selectedItem => selectedItem.id === item.id),
    selectedSearchItems: state => state.selectedSearchItems,
    sortBy: state => state.searchForm.sortBy,
    lastSearchParams: state => state.lastSearchParams,
    listOfFolderIdsInLastSearchResult: state => state.listOfFolderIdsInLastSearchResult,
    pathsForNewFolderHitsHasBeenLoaded: state => state.pathsForNewFolderHitsHasBeenLoaded,
    getlastUsedSortBy: state => state.lastUsedSortBy,
    isAllTypesFilter: state => state.allTypesFilter,
  },
  actions: {
    setDefaultDateRangeField: (context, payload: string | undefined) => {
      context.commit('setDefaultDateRangeField', payload);
      const defaultDateRangeFieldChoices = getDefaultDateRangeFieldChoices(context.rootGetters.mdfs);
      const dateFilterFieldChoice = defaultDateRangeFieldChoices.find(choice => choice.value === payload);
      const dateFilterField = dateFilterFieldChoice
        ? { key: dateFilterFieldChoice.value, label: dateFilterFieldChoice.label, mdf: dateFilterFieldChoice.customMdf }
        : undefined;
      setDateFilterField(context, dateFilterField);
    },
    resetSearch: async context => {
      context.commit('resetSearchMap');
      context.dispatch('clearAllFilters').catch(err => console.log(err));
      context.commit('clearSearchString');
      context.commit('setSameTimeFilter', false);
      // Perform the actual fresh search.
      EventBus.emit(EVENTS.DO_SEARCH);
    },
    singleItemSearch: async (context, payload: { applicationContext: ApplicationContext, itemId: string, quickPreviewMediaPlayerOps: ItemMediaPlayerOps }) => {
      const { applicationContext, itemId, quickPreviewMediaPlayerOps } = payload;
      // First determine if we already did this search, to avoid unnecessary searches when user
      // swaps between view types for example.
      if (context.state.itemSearch.results.hits.length === 1 && context.state.itemSearch.results.hits[0].id === itemId) {
        return;
      }

      context.commit('resetSearch');
      context.commit('setIsLoadingSearch', true);
      context.commit('clearSelectedSearchItems');
      try {
        const item = await getItem(itemId);
        try {
          const latestVersion = await getLatestItemVersion(itemId);
          if (latestVersion.id !== itemId) {
            item.latestVersionId = latestVersion.id;
          }
        } catch(err) {
          console.warn(`Could not get latest version of item ${itemId}`, err);
        }
        item.routerLink = generateRouterLink(item.id, context.getters.lastSearchParams, item.itemType);
        const currentFolder = folders.currentFolder;
        const isProxyMode = context.getters.isProxyWorkflowEnabled;
        injectItemIntoSearchResultMutation(context.commit, { isProxyMode, applicationContext, item, shouldUpdateExisting: false, currentFolder });
        void loadItemInQuickPreview({ item, quickPreviewMediaPlayerOps });
        context.commit('addToSelectedSearchItems', item);
      } catch (err: any) {
        if (err?.response?.data?.error?.statusCode === 404) {
          context.commit('setCustomNoResultMessage', `Item with id '${itemId}' could not be found.`);
        } else {
          logErrorAndShowToast(err?.response?.data?.error?.message || 'Could not load item search result', err, 'ex in singleItemSearch');
        }
      } finally {
        context.commit('setIsLoadingSearch', false);
      }
    },
    injectItemIntoSearchResult: ({ commit, dispatch, rootGetters }, payload: InjectItemIntoSearchPayload) => {
      injectItemIntoSearchResultMutation(commit, payload);
      if (isInElectron(payload.applicationContext)) {
        return loadFolderPathForItem(payload.isProxyMode, payload.applicationContext, dispatch, payload.item);
      }
    },
    insertUploadItemIntoSearch: (context, payload: { applicationContext: ApplicationContext, uploadItem: UploadItem, isProxyMode: boolean, quickPreviewMediaPlayerOps: ItemMediaPlayerOps }) => {
      const { applicationContext, uploadItem, isProxyMode, quickPreviewMediaPlayerOps } = payload;
      uploadItem.addCreatedItemCallback(callback => {
        void insertUploadItemIntoSearch(context, { isProxyMode, applicationContext, uploadItem: callback.uploadItem, addRouterLink: true, quickPreviewMediaPlayerOps });
      });
    },
    updateSearchHitThumbnailById: async (context, payload: string) => {
      const searchHit = context.state.itemSearch.results.hits.find(hit => hit.id === payload);
      if (!searchHit) return;
      const obj = await getItemFields(payload, ['thumbnail']);
      searchHit.thumbnail = obj.thumbnail;
    },
    updateSearchHitWithMedia: async (context, payload: SearchResultHit | LoadedItem) => {
      try {
        const retrievedFields: (keyof LoadedItem)[] = [
          'proxy', 'thumbnail', 'thumbnailStrip', 'itemState', 'transcodingState', 'mediaSize', 'mediaDuration',
          'hasEditProxy', 'hasProxy', 'editProxyIngested', 'editProxyAvailable', 'itemType', 'aaf', 'media',
          'editProxyFormat', 'id', 'proxySize', 'keldaIngest', 'highRes', 'editProxyUrl', 'mediaType', 'taskEntries',
        ];

        const partialItem = await getItemFields(payload.id, retrievedFields);
        const update: UpdateSearchHitById = {
          id: payload.id,
          update: partialItem,
          retrievedFields,
        };

        context.commit('updateSearchHitById', update);
        context.commit('updateCurrentItem', update);
        context.commit('updateCurrentPreviewItem', update);
      } catch (err) {
        logErrorAndShowToast('Was unable to update search hit with media details', err, 'ex in updateSearchHitWithMedia');
      }
      return;
    },
    searchResultsReceived: async (context, payload: {
      applicationContext: ApplicationContext,
      results: SearchResultViewModel,
      isProxyMode: boolean,
    }) => {
      context.commit('appendSearchHits', payload.results);

      // For each result hit, load the folder path from the server.
      void context.dispatch('processForLocalMedia', payload);
    },
    processForLocalMedia: async (context, payload: {
      isProxyMode: boolean,
      applicationContext: ApplicationContext,
      hits: SearchResultHit[],
    }) => {
      const { applicationContext, hits, isProxyMode } = payload;
      if (!isInAdobeApp(applicationContext) && !isInElectron(applicationContext)) {
        return;
      }

      /*
        Note, the asynchronous error handling of loadFolderPaths() errors here
        is intentional, because waiting for the server here would cause
        undesirable UI performance issues.

        However, as a side-effect, we're committing an item search state that
        is still asynchronously being modified by loadFolderPaths(), to the
        itemSearch() mutation.

        TODO: This is a violation of the Vuex API contact, and we should find
        a better way of doing this that both is compliant with the Vuex API
        contract and does not sacrifice UI performance.
      */
      if (!hits) {
        return;
      }

      if (isInElectron(applicationContext)) {
        // Only use folder info in Mimir Desktop to create the local link structure on disk.
        // We deliberately stopped using it in Premiere since it does not add much value, causes
        // extra stat calls, and makes the relink button appear.
        await loadFolderPaths(isProxyMode, applicationContext, context.dispatch, hits).catch(err => console.error(err));
      }

      await context.dispatch('initLocalMediaStatesAndPaths', hits.map(hit => hit.id)).catch(noop);
    },
    doStickySearch: async (context, payload: string) => {
      const parseResult = separateFiltersFromSearchString(payload);
      parseResult.parsedFilters.forEach(p => p.value = `${p.value}${filterDelimiter}${extraInfoSticky}`);
      const newSearchString = `${parseResult.remainingSearchString} ${parseResult.parsedFilters.map(p => `${p.token}:${p.value} `).join('')}`.trim();
      context.commit('setSearchString', newSearchString);

      // Force clear all filters including stickied values to have a fresh search again.
      clearFilters({ filters: context.rootState.filters, clearSticky: true });

      EventBus.emit(EVENTS.DO_SEARCH);
    },
    doCleanSearch: async (context, payload: string) => {
      context.commit('setSearchString', payload);
      await context.dispatch('clearAllFilters', { root: true });
      EventBus.emit(EVENTS.DO_SEARCH);
    },
    updatePersonWithRouterLink: (context, person: PersonResource) => {
      addRouterLink_([person], {});
    },
    createPlaceHolderAndSearchHit: ({ state, commit, getters }, payload: {
      isProxyMode: boolean,
      applicationContext: ApplicationContext,
      item: Partial<ItemCreationRequestDto>,
      inject: boolean,
    }) => {
      const { applicationContext, item, inject, isProxyMode } = payload;
      return new Promise((resolve, reject) => {
        createPlaceholder(item).then(response => {
          showToast('New item created', TOAST.SUCCESS);
          const searchHit: Item = {
            name: '',
            itemType: ItemType.FILE,
            mediaDuration: 0,
            thumbnail: getThumbUrl('', response.itemType),
            proxy: null,
            ...response,
            itemState: ItemState.NEW,
            routerLink: generateRouterLink(response.id, state.lastSearchParams, ItemType.FILE),
          };

          if (inject) {
            const currentFolder = folders.currentFolder;
            injectItemIntoSearchResultMutation(commit, { isProxyMode, applicationContext, item: searchHit, shouldUpdateExisting: false, currentFolder });
          }

          resolve(searchHit);
        }).catch(err => {
          const errorMessageFromServer = (err as Partial<AxiosError>)?.response?.data?.error?.message;
          showToast(errorMessageFromServer ?? 'Something went wrong creating new item', TOAST.ERROR);
          reject(err);
        });
      });
    },
    injectProgressInfo: ({ state }, {itemId, progressInfo}: { itemId: string, progressInfo?: ProgressInfo}) => {
      for (const hit of state.itemSearch.results.hits) {
        if (hit.id === itemId) {
          Vue.set(hit, 'progressInfo', progressInfo ?? { status: 'Preparing ..' });
          break;
        }
      }
    }
  }
};

const insertUploadItemIntoSearch = (context: ActionContext<SearchModuleState, State>, payload: {
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  uploadItem: UploadItem,
  addRouterLink: boolean,
  quickPreviewMediaPlayerOps: ItemMediaPlayerOps,
}) => {
  const { state, dispatch, rootState, commit } = context;
  const { applicationContext, uploadItem, addRouterLink, isProxyMode } = payload;
  const hits = state.itemSearch.results?.hits;
  if (hits) {
    if (!doesItemBelongInCurrentFolder(uploadItem, folders.currentFolder)) return;

    let index = -1;
    let existingItem: SearchResultHit | undefined = undefined;
    // First check if we've optimistically inserted a placeholder with a fake id.
    const fakeItemIndex = hits.findIndex(hit => hit.id === uploadItem.localId);
    if (fakeItemIndex >= 0) {
      index = fakeItemIndex;
    } else {
      // Otherwise check if we have one with a proper id we need to replace.
      const realItemIndex = hits.findIndex(hit => hit.id === uploadItem.id);
      if (realItemIndex >= 0) {
        existingItem = hits[realItemIndex];
        index = realItemIndex;
      }
    }

    const searchHit: Item = {
      vttUrl: '',
      itemType: uploadItem.derivedItemType,
      name: uploadItem.title,
      metadata: uploadItem.metadata as ItemMetaData,
      id: uploadItem.id,
      thumbnail: existingItem?.thumbnail ?? '',
      operationsLink: '',
      originalFileName: uploadItem.file.name,
      proxy: null,
      itemState: uploadItem.itemState !== null ? uploadItem.itemState : ItemState.NEW,
      createdOn: new Date().toISOString(),
      mediaDuration: 0,
      routerLink: addRouterLink ? generateRouterLink(uploadItem.id, context.getters.lastSearchParams, uploadItem.derivedItemType) : undefined,
      folderParents: uploadItem.folderParents ?? [],
      createdBy: uploadItem.createdBy,
      languageCode: uploadItem.itemAnalyticsProperties?.languageCode,
      visibleTo: uploadItem.visibleTo
    };

    if (isInElectron(applicationContext)) {
      void loadFolderPathForItem(isProxyMode, applicationContext, dispatch, searchHit);
    }
    // Object was to be replaced. Update the selection references
    if (index >= 0) {
      const updatePayload: UpdateSearchHitById = {
        id: uploadItem.id,
        update: searchHit,
      };
      updateSearchHitByIdMutation(commit, {
        id: uploadItem.id,
        update: updatePayload,
      });

      // Also update the reference in the quick preview in case the "fake" object is loaded there too.
      if (rootState.searchPlayer.currentItem?.id === uploadItem.localId) {
        commit('setSearchItem', { item: searchHit, quickPreviewMediaPlayerOps: payload.quickPreviewMediaPlayerOps });
      }
    } else {
      return injectItemIntoSearchResult(dispatch, {
        isProxyMode,
        applicationContext,
        item: searchHit,
        shouldUpdateExisting: false,
        currentFolder: folders.currentFolder,
      });
    }
  }
};

type InjectItemIntoSearchPayload = {
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  item: SearchResultHit,
  shouldUpdateExisting: boolean,
  currentFolder: FolderViewModel | null,
};

export const injectItemIntoSearchResult = (dispatch: Dispatch, payload: InjectItemIntoSearchPayload) => {
  return dispatch('injectItemIntoSearchResult', payload);
};

const injectItemIntoSearchResultMutation = (commit: Commit, payload: InjectItemIntoSearchPayload) => {
  commit('injectItemIntoSearchResult', payload);
};

export type UpdateSearchHitById = {
  id: string;
  update: Partial<LoadedItem> & Pick<LoadedItem, 'id'>;
  retrievedFields?: (keyof LoadedItem)[]; // fields that were fetched from the server
  itemIdToReplaceBy?: string;
};

export const updateSearchHitByIdMutation = (commit: Commit, payload: UpdateSearchHitById) => {
  commit('updateSearchHitById', payload);
};

export type ReplaceSearchHitById = {
  itemIdToReplace: string,
  newItem: SearchResultHit,
};

export const replaceSearchHitByIdMutation = (commit: Commit, payload: ReplaceSearchHitById) => {
  commit('replaceSearchHitById', payload);
};

/**
 * Checks if the given item is a child of the currently loaded folder.
 * Returns true if:
 * - There is no current folder loaded
 * - Item doesn't have any folder parents
 * - Item is a child of the currently loaded folder
 */
export const doesItemBelongInCurrentFolder = (item: { folderParents: string[] | null | undefined }, currentFolder: FolderViewModel | null) => {
  const uploadItemParents = item.folderParents ?? [];
  if (currentFolder && uploadItemParents.length > 0 && !(uploadItemParents.includes(currentFolder.id))) {
    return false;
  }
  return true;
};

const extractSortFieldsInfo = (params: SortParameters[], mdfs: MetaDataSchema[]) => {
  const sortFieldsInfo: Record<string, { id: string }> = {};
  for (const param of params) {
    // If mdfSource is a regular schema then we need to extract the id. So that we can extract
    // the field value from websocket item metadata.
    if (param?.fieldId && param.mdfSource && param.mdfSource !== SYSTEM_FIELD_ID_QUERYPARAM && param.mdfSource !== TECHNICAL_FIELD_ID_QUERYPARAM) {
      const mdfToUse = mdfs.find(mdf => mdf.label === param.mdfSource);
      if (!mdfToUse) continue;
      if (mdfToUse.fields) {
        const field = mdfToUse.fields.find(f => f.fieldId === param.fieldId);
        if (field) {
          sortFieldsInfo[param.fieldId] = {
            id: field.id
          };
        }
      }
    }
  }
  return sortFieldsInfo;
};
